import clsx from 'clsx';
import { KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { KeyCode } from '@vestico/utils';

import { SizeRecommendation } from '@vestico/model';

import { CustomCSSProperties } from '@vestico/shared';
import globalStyle from '@vestico/shared/src/layouts/utils.module.css';

import style from './SizeRecommendationDetail.module.css';

export interface SizeRecommendationDetailProps {
  sizeRecommendation: SizeRecommendation;
  index: number;
  selected: boolean;
  onSelect: (index: number) => void;
}

const MAIN_RECOMMENDATION_COLOR = 'var(--color)';
const OTHER_RECOMMENDATION_COLOR = 'var(--color-secondary)';

export const SizeRecommendationDetail = ({ sizeRecommendation, index, selected, onSelect }: SizeRecommendationDetailProps) => {
  const isMainRecommendation = useMemo(() => index === 0, [index]);
  const { variantOptions, confidence, isLowConfidenceResult } = sizeRecommendation;

  const handleSelect = useCallback(() => onSelect(index), [index, onSelect]);
  const handleKeyUp = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (e.code === KeyCode.enter) {
        handleSelect();
      }
    },
    [handleSelect]
  );

  const roundBGColor = useMemo(() => (isMainRecommendation ? MAIN_RECOMMENDATION_COLOR : OTHER_RECOMMENDATION_COLOR), [isMainRecommendation]);
  const [sizeRecValue, setSizeRecValue] = useState('');

  useEffect(() => {
    setSizeRecValue(`${(isLowConfidenceResult ? 0.2 : confidence) * 100}%`);
  }, [confidence, isLowConfidenceResult]);

  const cssProperties = useMemo(() => {
    const css: CustomCSSProperties = {};
    if (sizeRecValue) {
      css['--size-rec-level'] = sizeRecValue;
    }

    css['--bar-color'] = `${roundBGColor}`;

    return css;
  }, [roundBGColor, sizeRecValue]);

  return (
    <div
      className={clsx(style.root, { [style.main]: isMainRecommendation, [style.selected]: selected })}
      onClick={handleSelect}
      onKeyUp={handleKeyUp}
      tabIndex={0}
      role="button"
    >
      <div className={clsx(style.confidenceBarWrapper)} style={cssProperties}>
        <div className={clsx(style.bar, style.confidenceBarBackground)} />
        <div className={clsx(style.bar, style.confidenceBar)} />
        <div className={clsx(style.confidenceBarValues)}>
          {variantOptions.map((variantOption) => {
            return (
              <div key={variantOption} className={clsx(style.confidenceBarValue, globalStyle.textNormal, globalStyle.textBold)}>
                {variantOption}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
